import { Auth } from "aws-amplify";
import { Formik, Field, Form } from "formik";
import { Link, useNavigate } from "react-router-dom";
import { useState } from "react";
import * as Yup from "yup";
import classNames from "classnames"
import { loginBg } from "../../assets/images";
import { logoIcon } from "../../assets/icons";
import { EMPTY_STRING, ROUTES } from "../../constants";
import { Alert } from "../../components/molecules";
import { Helmet } from "react-helmet-async";
import Spinner, { SPINNER_NAMES } from "../../components/organisms/Spinner";
import { actions as spinnerActions } from "../../components/organisms/Spinner/spinner.reducers";
import { useDispatch } from "react-redux";
import { VALIDATION_MESSAGES } from "../../constants";

const { addSpinner, removeSpinner } = spinnerActions;
const { LOGIN, SET_PASSWORD } = ROUTES;
const { FORGOT_PASSWORD } = SPINNER_NAMES;
const { INVALID_EMAIL, EMAIL_REQUIRED, VALIDATE_AUTHORIZATION } = VALIDATION_MESSAGES

const ForgotPassword = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [isDisabled, setIsDisabled] = useState(true);
  const [alertData, setAlertData] = useState();
  const [isSpinnerActive, setIsSpinnerActive] = useState(false);

  const redirectToSetPasswordScreen = (email) => {
    const completeRedirectUrl = `${SET_PASSWORD}?email=${encodeURIComponent(
      email
    )}`;
    navigate(completeRedirectUrl, {
      replace: true,
    });
    dispatch(removeSpinner(FORGOT_PASSWORD));
  };

  const validationSchema = Yup.object({
    email: Yup.string(INVALID_EMAIL)
      .email(INVALID_EMAIL)
      .required(EMAIL_REQUIRED),
    code: Yup.string().test(
      "isValid",
      VALIDATE_AUTHORIZATION,
      (val) => (!isDisabled ? val?.length : true)
    ),
  });

  return (
    <>
      <Helmet>
        <title>Forget Password | TavaJS</title>
      </Helmet>
      <div className="auth-container min-h-screen relative flex items-center">
        <img
          src={loginBg}
          className="w-full h-full absolute top-0 left-0 object-cover pointer-events-none"
          alt="bgimage"
        />
        <div className="auth-box text-gray-50 relative w-full mx-auto py-12 px-6 sm:w-1/2 md:w-1/2 lg:w-1/3 2xl:w-1/4">
          <Formik
            initialValues={{
              email: EMPTY_STRING,
            }}
            validationSchema={validationSchema}
            onSubmit={(values) => {
              setAlertData();
              dispatch(addSpinner(FORGOT_PASSWORD));
              Auth.forgotPassword(values.email)
                .then(() => {
                  setIsDisabled(false);
                  redirectToSetPasswordScreen(values.email);
                })
                .catch((error) => {
                  dispatch(removeSpinner(FORGOT_PASSWORD));
                  setAlertData({ message: error.message });
                });
            }}
          >
            {({ values, handleBlur, handleChange, errors, touched }) => (
              <Form>
                <div className="auth-header mb-8">
                  {alertData && (
                    <div className="my-2">
                      <Alert
                        type={alertData.type}
                        message={alertData.message}
                        handleOnClose={setAlertData}
                      />
                    </div>
                  )}
                  <div className="logo mb-6">
                    <img src={logoIcon} width={52} alt="logo" />
                  </div>
                  <h1 className="text-3xl leading-9 font-extrabold mb-2">
                    Reset Your Password
                  </h1>
                  <span className="text-6">
                    Or&nbsp;
                    <Link
                      to={LOGIN}
                      className="text-indigo-200 hover:text-indigo-500"
                    >
                      login instead
                    </Link>
                  </span>
                </div>
                <div className="auth-body">
                  <div className="form-group mb-2">
                    <label className="text-sm font-medium mb-1 block">
                      Email
                    </label>
                    <Field
                      type="text"
                      name="email"
                      value={values.email}
                      className={classNames(
                        "form-control disabled:opacity-75 disabled:cursor-not-allowed border-2",
                        {
                          "border-red-600":
                            touched.email && errors.email,
                        }
                      )}
                      placeholder="Your Email Address"
                      onBlur={handleBlur}
                      onChange={handleChange}
                    />
                    {touched.email && errors.email && (
                      <div className="font-medium text-red-500 text-sm absolute">
                        {errors.email}
                      </div>
                    )}
                    <div className="mt-6">
                      <span>
                        <button
                          className="relative btn btn-primary block w-full text-center"
                          type="submit"
                          disabled={isSpinnerActive}
                        >
                          <Spinner
                            name={FORGOT_PASSWORD}
                            setIsSpinnerActive={setIsSpinnerActive}
                            persistSize
                          >
                            Send Password Reset
                          </Spinner>
                        </button>
                      </span>
                    </div>
                  </div>
                </div>
              </Form>
            )}
          </Formik>
        </div>
      </div>
    </>
  );
};

export default ForgotPassword;
